import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Navbar from '../Items/Navbar';
import AuthContext from '../shared/AuthContext';
import PageMeta from '../seo/PageMeta';
import { PLANS, PLAN_FEATURE_ROWS } from '../../config/plans';
import { AI_DAILY_LIMITS } from '../../config/aiLimits';
import { useStartCheckout } from '../../hooks/useStartCheckout';
import apiClient, { billingService } from '../shared/hooks/AuthService';
import './CheckoutPage.css';

const PRO_PLAN = PLANS.find((p) => p.id === 'pro');

export default function CheckoutPage() {
  const { user, isAuthenticated, isLoading: authLoading } = useContext(AuthContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const interval = searchParams.get('interval') === 'yearly' ? 'yearly' : 'monthly';
  const planKey = searchParams.get('plan') || 'pro';

  const { startCheckout, loading, error } = useStartCheckout();
  const [usage, setUsage] = useState(null);
  const [billingConfig, setBillingConfig] = useState(null);

  useEffect(() => {
    billingService.getConfig().then((r) => setBillingConfig(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    apiClient.get('/api/users/me/usage').then((r) => setUsage(r.data)).catch(() => {});
  }, [isAuthenticated]);

  const price = useMemo(() => {
    if (!PRO_PLAN) return { amount: 0, label: 'mo' };
    return interval === 'yearly'
      ? { amount: PRO_PLAN.yearly, label: 'year', perMonth: Math.round(PRO_PLAN.yearly / 12) }
      : { amount: PRO_PLAN.monthly, label: 'mo' };
  }, [interval]);

  const setInterval = (yearly) => {
    const next = new URLSearchParams(searchParams);
    next.set('interval', yearly ? 'yearly' : 'monthly');
    next.set('plan', planKey);
    setSearchParams(next, { replace: true });
  };

  const currentPlan = (usage?.plan || 'FREE').toUpperCase();
  const alreadyPro = currentPlan === 'PRO' || currentPlan === 'TEAM';

  const handlePay = () => {
    startCheckout({ plan: planKey, interval, loginRedirect: `/checkout?plan=${planKey}&interval=${interval}` });
  };

  const gatewayLabel = billingConfig?.demoMode
    ? 'Demo mode (no card — instant Pro for testing)'
    : billingConfig?.stripeConfigured
      ? 'Stripe secure checkout'
      : 'Configure Stripe on the API to accept live payments';

  return (
    <div className="checkout-page">
      <PageMeta title="Checkout" description="Upgrade to QnaRead Pro with secure checkout" />
      <Navbar userProfile={user} loading={authLoading} />

      <main className="checkout-page-main">
        <div className="checkout-page-grid">
          <section className="checkout-summary-card">
            <p className="checkout-eyebrow">Secure checkout</p>
            <h1>Upgrade to Pro</h1>
            <p className="checkout-lead">
              Mind map AI search, {AI_DAILY_LIMITS.pro} AI requests per day, and advanced analytics — billed via
              our payment partner.
            </p>

            <div className="checkout-interval-toggle">
              <button
                type="button"
                className={interval === 'monthly' ? 'active' : ''}
                onClick={() => setInterval(false)}
              >
                Monthly
              </button>
              <button
                type="button"
                className={interval === 'yearly' ? 'active' : ''}
                onClick={() => setInterval(true)}
              >
                Yearly · save ~17%
              </button>
            </div>

            <div className="checkout-line-items">
              <div className="checkout-line">
                <span>QnaRead Pro ({interval})</span>
                <strong>
                  ${price.amount}/{price.label}
                </strong>
              </div>
              {interval === 'yearly' && price.perMonth != null && (
                <p className="checkout-equiv">About ${price.perMonth}/month billed annually</p>
              )}
            </div>

            <ul className="checkout-feature-list">
              {PRO_PLAN?.features.slice(0, 5).map((f) => (
                <li key={f}>{f}</li>
              ))}
            </ul>

            <p className="checkout-gateway-note">
              <span className="checkout-lock" aria-hidden>
                🔒
              </span>{' '}
              {gatewayLabel}. Card details are handled on the payment provider&apos;s page — not on QnaRead.
            </p>
          </section>

          <aside className="checkout-pay-card">
            <h2>Payment</h2>
            {!isAuthenticated && !authLoading && (
              <>
                <p className="checkout-muted">Sign in to continue to payment.</p>
                <Link
                  to={`/login?redirect=${encodeURIComponent(`/checkout?plan=${planKey}&interval=${interval}`)}`}
                  className="checkout-btn checkout-btn-primary"
                >
                  Sign in to checkout
                </Link>
              </>
            )}

            {isAuthenticated && alreadyPro && (
              <>
                <p className="checkout-muted">
                  You&apos;re already on <strong>{currentPlan}</strong>.
                </p>
                <Link to="/settings?tab=billing" className="checkout-btn checkout-btn-secondary">
                  Manage billing
                </Link>
              </>
            )}

            {isAuthenticated && !alreadyPro && (
              <>
                <p className="checkout-total">
                  Total due today
                  <span>
                    ${price.amount}
                    <small>/{price.label}</small>
                  </span>
                </p>
                <p className="checkout-muted checkout-account">
                  Account: <strong>{user?.email}</strong>
                </p>
                <button
                  type="button"
                  className="checkout-btn checkout-btn-primary"
                  onClick={handlePay}
                  disabled={loading}
                >
                  {loading ? 'Redirecting to payment…' : 'Continue to payment'}
                </button>
                {error && (
                  <p className="checkout-error" role="alert">
                    {error}
                  </p>
                )}
                <p className="checkout-fine-print">
                  By continuing you agree to our{' '}
                  <Link to="/terms">Terms</Link> and{' '}
                  <Link to="/refund">Refund policy</Link>. Subscription renews until canceled in the billing
                  portal.
                </p>
              </>
            )}

            <Link to="/pricing" className="checkout-back-link">
              Compare all plans
            </Link>
          </aside>
        </div>

        <section className="checkout-compare-mini">
          <h3>What Pro unlocks</h3>
          <table>
            <thead>
              <tr>
                <th>Feature</th>
                <th>Free</th>
                <th>Pro</th>
              </tr>
            </thead>
            <tbody>
              {PLAN_FEATURE_ROWS.filter((r) =>
                ['AI explain in depth', 'Mind map AI search', 'Analytics & JSON export'].includes(r.label)
              ).map((row) => (
                <tr key={row.label}>
                  <td>{row.label}</td>
                  <td>{String(row.free)}</td>
                  <td>{String(row.pro)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      </main>
    </div>
  );
}
